import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faImage, 
  faQuestionCircle,
  faChevronDown,
  faThumbtack
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/group/CreatePostModal.css';
import groupService from '../../api/groupService';
import apiClient from '../../api/apiClient';
import { useGroup } from '../../context/GroupContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import PostSuccessMessage from '../common/PostSuccessMessage';

const CreatePostModal = ({ isOpen, onClose, groupId, editingPost, postTags = [], onPostCreated, activeTag = 'all' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [postTag, setPostTag] = useState('Update'); // Selected tag
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showHelperTooltip, setShowHelperTooltip] = useState(false);
  const [showAIField, setShowAIField] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiContext, setAiContext] = useState(''); // Store previous AI context for modifications
  const [isTyping, setIsTyping] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCreateTagInline, setShowCreateTagInline] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isPinned, setIsPinned] = useState(false); // Pin post toggle
  const [showPinTooltip, setShowPinTooltip] = useState(false); // Tooltip for pin toggle
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image: null,
    imageCaption: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false); // Track if image was explicitly removed
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Get group info from context
  const { adminGroup } = useGroup();
  
  // Get snackbar context
  const { showPostUpdateSnackbar } = useSnackbar();
  
  // Default tag options - always available
  const defaultTagOptions = [
    { value: 'Announcement', label: 'Announcement' },
    { value: 'Update', label: 'Update' },
    { value: 'Mazal Tov', label: 'Mazal Tov' }
  ];
  
  // Combine default tags with custom tags
  const customTagOptions = postTags.map(tag => ({
    value: tag.name,
    label: tag.name
  }));
  
  // Merge default and custom tags (defaults first, then custom)
  const tagOptions = [...defaultTagOptions, ...customTagOptions];
  
  // Get group logo or default
  const getGroupLogo = () => {
    console.log('🖼️ Getting group logo, adminGroup:', adminGroup);
    console.log('🖼️ Logo URL:', adminGroup?.logo_url);
    
    // If we have a valid logo URL, use it
    if (adminGroup?.logo_url && adminGroup.logo_url.trim() !== '') {
      return adminGroup.logo_url;
    }
    
    // Return Yesod default logo as fallback
    return 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesodsybol%20(1).jpeg';
  };
  
  // Get group name
  const getGroupName = () => {
    return adminGroup?.name || 'Community';
  };

  // Reset form when modal opens/closes or populate with editing data
  useEffect(() => {
    if (isOpen) {
      setError('');
      
      if (editingPost) {
        // Populate form with existing post data for editing
        setFormData({
          title: editingPost.title || '',
          content: editingPost.content || '',
          image: null, // Don't prefill images for editing (complex to handle)
          imageCaption: editingPost.image_caption || ''
        });
        setPostTag(editingPost.tag || 'Update');
        setIsPinned(editingPost.is_pinned || false); // Set pin state from editing post
        
        // If there's an existing image, set preview
        if (editingPost.image_url) {
          setImagePreview(editingPost.image_url);
        } else {
          setImagePreview(null);
        }
      } else {
        // Reset form for new post creation
        setFormData({
          title: '',
          content: '',
          image: null,
          imageCaption: ''
        });
        setImagePreview(null);
        
        // Set tag based on active filter or default to 'Update'
        if (activeTag && activeTag !== 'all') {
          // If user has filtered to a specific tag, use that tag
          setPostTag(activeTag);
        } else {
          // Otherwise default to 'Update'
          setPostTag('Update');
        }
        
        setIsPinned(false); // Reset pin state for new posts
      }
      
      setImageRemoved(false); // Reset image removed flag
      setShowTagDropdown(false);
      setShowHelperTooltip(false);
      setShowPinTooltip(false); // Reset pin tooltip
      setShowAIField(false);
      setAiPrompt('');
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, editingPost, activeTag]);

  // Properly size textarea when content changes (including when editing posts are loaded)
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const textarea = textareaRef.current;
      // Small delay to ensure content is rendered
      setTimeout(() => {
        textarea.style.height = 'auto';
        const maxTextareaHeight = 400; // Keep consistent with handleTextareaChange
        const newHeight = Math.min(textarea.scrollHeight, maxTextareaHeight);
        textarea.style.height = newHeight + 'px';
      }, 0);
    }
  }, [isOpen, formData.content]);

  // Close helper tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHelperTooltip) {
        // Check if the click is not on the helper icon
        if (!event.target.closest('.helper-icon')) {
          setShowHelperTooltip(false);
        }
      }
    };

    if (showHelperTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelperTooltip]);

  // Handle textarea change and auto-resize
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    setFormData(prev => ({
      ...prev,
      content: textarea.value
    }));
    
    // Auto-resize textarea with constraints
    textarea.style.height = 'auto';
    
    // Calculate max available height (respect CSS max-height and leave room for other modal content)
    const maxTextareaHeight = 400; // Conservative max height to ensure save button stays visible
    const newHeight = Math.min(textarea.scrollHeight, maxTextareaHeight);
    textarea.style.height = newHeight + 'px';
    
    setError('');
  };

  // Handle title change
  const handleTitleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      title: e.target.value
    }));
    setError('');
  };

  // Typing effect function
  const startTypingEffect = async (title, content) => {
    setIsTyping(true);
    
    // Clear existing content
    setFormData(prev => ({
      ...prev,
      title: '',
      content: ''
    }));
    
    // Type title first
    if (title) {
      for (let i = 0; i <= title.length; i++) {
        setFormData(prev => ({
          ...prev,
          title: title.substring(0, i)
        }));
        await new Promise(resolve => setTimeout(resolve, 5)); // 5ms per character (was 30ms)
      }
      
      // Small pause between title and content
      await new Promise(resolve => setTimeout(resolve, 50)); // 50ms (was 200ms)
    }
    
    // Type content
    if (content) {
      for (let i = 0; i <= content.length; i++) {
        setFormData(prev => ({
          ...prev,
          content: content.substring(0, i)
        }));
        await new Promise(resolve => setTimeout(resolve, 3)); // 3ms per character (was 20ms)
      }
    }
    
    setIsTyping(false);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      // Check file type - only allow supported formats
      const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!supportedFormats.includes(file.type.toLowerCase())) {
        setError('Unsupported image format. Please use JPG, PNG, GIF, or WebP.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        image: file
      }));
      
      // Reset the imageRemoved flag since a new image was selected
      setImageRemoved(false);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  // Remove image
  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      imageCaption: ''
    }));
    setImagePreview(null);
    setImageRemoved(true); // Mark that image was explicitly removed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const title = formData.title || '';
    const content = formData.content || '';
    
    // Validate that either title or content is provided
    if (!title.trim() && !content.trim()) {
      setError('Please add a title or content to share with your community');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Prepare the post data - ALWAYS use JSON like events do
      const postData = {
        tag: postTag,
        group_id: groupId,
        is_pinned: isPinned
      };
      
      if (title.trim()) {
        postData.title = title.trim();
      }
      if (content.trim()) {
        postData.content = content.trim();
      }
      
      // Handle image upload - convert to base64 like events do
      if (formData.image && formData.image instanceof File) {
        console.log('📤 Creating post with image - converting to base64:', {
          name: formData.image.name,
          type: formData.image.type,
          size: `${Math.round(formData.image.size / 1024)} KB`
        });
        
        // Validate the file before processing
        if (formData.image.size === 0) {
          throw new Error('Selected file is empty');
        }
        
        if (!formData.image.type.startsWith('image/')) {
          throw new Error('Selected file is not a valid image');
        }
        
        // Convert file to base64
        const base64String = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.image);
        });
        
        postData.image = base64String;
        console.log('✅ Image converted to base64 successfully');
        
        if (formData.imageCaption) {
          postData.image_caption = formData.imageCaption;
        }
      }

      // Create or update the post
      let response;
      if (editingPost) {
        // If image was explicitly removed, add the flag
        if (imageRemoved && !formData.image) {
          postData.removeImage = true;
          console.log('Sending removeImage flag to backend');
        }
        
        // Update existing post
        response = await groupService.updateCommunityPost(editingPost.id, postData);
      } else {
        // Create new post
        response = await groupService.createCommunityPost(groupId, postData);
      }

      // Reset form
      setFormData({
        title: '',
        content: '',
        image: null,
        imageCaption: ''
      });
      setImagePreview(null);
      setImageRemoved(false); // Reset the image removed flag
      setPostTag('Update');
      setIsPinned(false); // Reset pin toggle
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      // Call success callback with proper data
      if (onPostCreated) {
        // For updates, pass the updated post data; for creates, pass the response data
        const postData = editingPost ? response.data : response.data;
        console.log('📤 CreatePostModal calling callback with:', postData);
        console.log('📤 Operation type:', editingPost ? 'UPDATE' : 'CREATE');
        console.log('📤 Response data:', response.data);
        onPostCreated(postData);
      }

      // Close modal
      onClose();

      // Show success message
      setShowSuccessMessage(true);
      
      // Show snackbar notification for refresh
      showPostUpdateSnackbar();

    } catch (error) {
      const action = editingPost ? 'updating' : 'creating';
      console.error(`❌ CreatePostModal: Error ${action} post:`, error);
      setError(error.message || `Failed to ${action === 'updating' ? 'update' : 'create'} post. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Close modal handler
  const handleClose = () => {
    if (!isLoading) {
      setError('');
      setAiPrompt('');
      onClose();
    }
  };

  // Handle create tag inline
  const handleCreateTagInline = async () => {
    if (!newTagName.trim()) return;
    
    try {
      const response = await groupService.createPostTag(groupId, {
        name: newTagName.trim(),
        icon: '📝',
        color: '#5669FF'
      });
      
      if (response.success) {
        // Use the newly created tag
        setPostTag(response.data.name);
        setNewTagName('');
        setShowCreateTagInline(false);
        setShowTagDropdown(false);
        
        // Notify parent to refresh tags (without page reload)
        if (onPostCreated) {
          // Call the callback to refresh the tag list
          onPostCreated({ justRefreshTags: true });
        }
      }
    } catch (err) {
      console.error('Error creating tag inline:', err);
      alert('Failed to create tag. Please try again.');
    }
  };

  // Handle AI generation
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setError('Please enter a prompt for AI generation');
      return;
    }

    setIsAIGenerating(true);
    setError('');

    try {
      console.log('🤖 AI Agent: Starting community post generation...');
      console.log('📝 Prompt:', aiPrompt);

      const requestData = {
        command: `YOU ARE A COMMUNITY POST CONTENT GENERATOR ONLY. DO NOT CHAT. DO NOT ASK QUESTIONS. DO NOT EXPLAIN WHAT YOU CAN OR CANNOT DO.

YOUR ONLY JOB: Generate or modify community post content. You are NOT the synagogue management AI assistant that creates classes and events.

❌ NEVER SAY: "I understand you're looking for..." 
❌ NEVER SAY: "I'm designed to help..."
❌ NEVER SAY: "Would you like me to..."
❌ NEVER SAY: "I'm not able to generate..."
❌ NO CONVERSATIONAL RESPONSES
❌ NO QUESTIONS OR CLARIFICATIONS  
❌ NO EXPLANATIONS OF CAPABILITIES
✅ ONLY GENERATE POST CONTENT
✅ ALWAYS USE TITLE: and CONTENT: FORMAT
✅ ALWAYS COMPLETE THE REQUEST

${aiContext ? `PREVIOUS CONTEXT FROM THIS CONVERSATION: ${aiContext}\n\n` : ''}USER REQUEST: ${aiPrompt.trim()}

${formData.title || formData.content ? `EXISTING POST TO MODIFY:
Title: ${formData.title}
Content: ${formData.content}

` : ''}INSTRUCTIONS:
- Generate or modify the community post based on the request
- If modifying existing content, improve/change only what was requested
- If creating new content, follow community guidelines
- ALWAYS respond with TITLE: and CONTENT: format
- NEVER ask questions or provide explanations
- NEVER return incomplete content or requests for clarification

FORMAT REQUIRED:
TITLE: [Complete title with emoji if appropriate]
CONTENT: [Complete post content following community guidelines]

COMMUNITY GUIDELINES:
🎯 Goal: Generate clear, warm, respectful, engaging community posts
📝 Structure: Opening line → Body (3-5 lines) → Closing (1-2 lines) = 5-8 sentences total
🎨 Formatting: Use **bold** for names/important details, *italic* for blessings/Hebrew words
� Tone: Warm, uplifting, communal. Use "we" and "our community"
📱 Content Types:
- 🎉 Mazal Tov: Celebrate fully, bless family, connect to community joy
- 🙏 Prayer Request: Share name, offer comfort, explain community support
- 📢 Updates: Highlight news, show appreciation, invite participation

GENERATE CONTENT NOW:`,
        groupId
      };

      console.log('🚀 AI Agent: Sending community post request');
      const response = await apiClient.post('/ai/command', requestData);
      console.log('✅ AI Agent: Response received:', response.data);

      // Check if we have any text content in the response
      const aiText = response.data.text || response.data.message || response.data.content || '';
      
      if (aiText && aiText.trim()) {
        console.log('🤖 Raw AI response:', aiText);
        
        // Parse the AI response to extract title and content
        let extractedTitle = '';
        let extractedContent = '';
        
        // Try to parse TITLE: and CONTENT: format
        const titleMatch = aiText.match(/TITLE:\s*(.+?)(?=\n|CONTENT:|$)/s);
        const contentMatch = aiText.match(/CONTENT:\s*(.+)$/s);
        
        if (titleMatch && contentMatch) {
          extractedTitle = titleMatch[1].trim();
          extractedContent = contentMatch[1].trim();
        } else {
          // Fallback: Split by line breaks and use first line as title
          const lines = aiText.split('\n').filter(line => line.trim());
          if (lines.length > 0) {
            extractedTitle = lines[0].replace(/^(TITLE:|##|#)?\s*/, '').trim();
            extractedContent = lines.slice(1).join('\n').replace(/^(CONTENT:)?\s*/, '').trim();
          } else {
            extractedTitle = '';
            extractedContent = aiText;
          }
        }
        
        // Clean up HTML tags from title (title should be plain text)
        extractedTitle = extractedTitle.replace(/<[^>]*>/g, '');
        
        // Clean up content but preserve basic formatting
        extractedContent = extractedContent
          .replace(/\*\*(.+?)\*\*/g, '$1') // Remove markdown bold
          .replace(/\*(.+?)\*/g, '$1') // Remove markdown italic
          .replace(/<[^>]*>/g, ''); // Remove other HTML tags
        
        console.log('📝 Parsed title:', extractedTitle);
        console.log('📝 Parsed content:', extractedContent);
        
        // Start typing effect
        await startTypingEffect(extractedTitle, extractedContent);
        
        // Save context for future modifications
        setAiContext(`User's last request: "${aiPrompt.trim()}". Current post state - Title: "${extractedTitle}" | Content: "${extractedContent.substring(0, 200)}${extractedContent.length > 200 ? '...' : ''}"`);
        
        // Keep AI field open and clear prompt for next use
        setAiPrompt('');
        
        // Show success message
        if (window.showToast) {
          window.showToast('Community post generated successfully!', 'success');
        }
      } else {
        throw new Error('No content received from AI service');
      }

    } catch (error) {
      console.error('❌ AI Agent: Error generating content:', error);
      setError(`AI Generation failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsAIGenerating(false);
    }
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="create-post-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-left">
            <button 
              className="helper-icon"
              onClick={() => setShowHelperTooltip(!showHelperTooltip)}
              type="button"
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
            {showHelperTooltip && (
              <div className="helper-tooltip">
                This post will appear in your community's feed, visible only to your members on the Yesod app
              </div>
            )}
          </div>
          <h2>{editingPost ? 'Edit Post' : 'Create Post'}</h2>
          <button 
            className="close-button"
            onClick={handleClose}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* User Info Section */}
        <div className="user-info-section">
          <div className="user-avatar">
            <img 
              src={getGroupLogo()} 
              alt="Group Logo"
              onError={(e) => {
                console.error('🖼️ Failed to load group logo:', e.target.src);
                // Set fallback to Yesod default logo if primary logo fails
                e.target.src = 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesodsybol%20(1).jpeg';
              }}
            />
          </div>
          <div className="user-details">
            <span className="user-name">
              {getGroupName()}
            </span>
            <div className="tag-selector" onClick={() => setShowTagDropdown(!showTagDropdown)}>
              <span className="tag-text">
                <span>
                  {tagOptions.find(opt => opt.value === postTag)?.label}
                </span>
                <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
              </span>
              
              {showTagDropdown && (
                <div className="tag-dropdown">
                  <div className="tag-dropdown-options">
                    {tagOptions.map(option => (
                      <div 
                        key={option.value}
                        className={`dropdown-option ${postTag === option.value ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('📝 Tag selection - Old tag:', postTag, 'New tag:', option.value);
                          setPostTag(option.value);
                          setShowTagDropdown(false);
                          setShowCreateTagInline(false);
                        }}
                      >
                        <span className="option-label">{option.label}</span>
                        {postTag === option.value && <span className="checkmark">✓</span>}
                      </div>
                    ))}
                  </div>
                  
                  {/* Divider */}
                  <div className="dropdown-divider"></div>
                  
                  {/* Create Tag Option */}
                  {!showCreateTagInline ? (
                    <div 
                      className="dropdown-option create-tag-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateTagInline(true);
                      }}
                    >
                      <span className="option-label">+ Create New Tag</span>
                    </div>
                  ) : (
                    <div className="create-tag-inline" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="Tag name..."
                        className="create-tag-input"
                        maxLength={30}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateTagInline();
                          }
                        }}
                        autoFocus
                      />
                      <div className="create-tag-buttons">
                        <button
                          type="button"
                          className="create-tag-btn"
                          onClick={handleCreateTagInline}
                          disabled={!newTagName.trim()}
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          className="cancel-tag-btn"
                          onClick={() => {
                            setShowCreateTagInline(false);
                            setNewTagName('');
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="post-form">
          {/* Title Section - Notion Style */}
          <div className="title-section">
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder="Add a title..."
              className="title-input"
              disabled={isLoading}
            />
          </div>

          {/* Text Content */}
          <div className="content-section">
            <textarea
              ref={textareaRef}
              name="content"
              value={formData.content}
              onChange={handleTextareaChange}
              placeholder="What would you like to tell your community?"
              className="content-textarea"
              rows="3"
              disabled={isLoading}
            />
          </div>

          {/* Image Preview */}
          {imagePreview && (
            <div className="image-preview-section">
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={removeImage}
                  disabled={isLoading}
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          {/* AI Generation Section */}
          <div className="ai-generation-section">
            {!showAIField ? (
              <button
                type="button"
                className="ai-generate-button"
                onClick={() => setShowAIField(true)}
                disabled={isLoading}
              >
                <img 
                  src="/assets/icons8-ai.svg" 
                  alt="AI" 
                  className="ai-icon"
                />
                Ask Yesod AI
              </button>
            ) : (
              <div className="ai-inline-field ai-inline-field-simple">
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ask AI to help you write something..."
                  className="ai-prompt-textarea-simple"
                  disabled={isAIGenerating}
                  rows="1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && aiPrompt.trim() && !isAIGenerating) {
                      e.preventDefault();
                      handleAIGenerate();
                    }
                  }}
                />
                <div className="ai-buttons-container">
                  {isAIGenerating ? (
                    <div className="ai-loading-state">
                      <div className="ai-spinner"></div>
                      <span>Yesod AI is working...</span>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="ai-submit-btn"
                        onClick={handleAIGenerate}
                        disabled={!aiPrompt.trim() || isAIGenerating}
                        title="Generate content"
                      >
                        →
                      </button>
                      <button
                        type="button"
                        className="ai-close-btn"
                        onClick={() => {
                          setShowAIField(false);
                          setAiPrompt('');
                        }}
                        title="Close AI field"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Add to Post Section */}
          <div className="add-to-post-section">
            <span className="add-to-post-label">Add to your post</span>
            <div className="add-to-post-buttons">
              <button
                type="button"
                className="add-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                title="Photo/Video"
              >
                <FontAwesomeIcon icon={faImage} />
              </button>
              
              {/* Pin Toggle */}
              <div className="pin-toggle-container">
                <label className="pin-toggle-label">
                  <FontAwesomeIcon icon={faThumbtack} className="pin-icon" />
                  <span className="pin-toggle-text">Pin</span>
                  <input
                    type="checkbox"
                    checked={isPinned}
                    onChange={(e) => setIsPinned(e.target.checked)}
                    disabled={isLoading}
                    className="pin-toggle-checkbox"
                  />
                  <span className="pin-toggle-slider"></span>
                </label>
                <button
                  type="button"
                  className="pin-helper-icon"
                  onClick={() => setShowPinTooltip(!showPinTooltip)}
                  onBlur={() => setTimeout(() => setShowPinTooltip(false), 200)}
                  title="What is pinning?"
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </button>
                {showPinTooltip && (
                  <div className="pin-tooltip">
                    Pinned posts appear at the top of the updates page in the app, making them more visible to all members.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            disabled={isLoading}
          />

          {/* Submit Button */}
          <button
            type="submit"
            className={`post-button ${(!(formData.title || '').trim() && !(formData.content || '').trim()) ? 'disabled' : ''}`}
            disabled={(!(formData.title || '').trim() && !(formData.content || '').trim()) || isLoading}
          >
            {isLoading ? (editingPost ? 'Updating...' : 'Posting...') : (editingPost ? 'Save Changes' : 'Post to App')}
          </button>
        </form>
      </div>
      
      {/* Success Message */}
      {showSuccessMessage && (
        <PostSuccessMessage 
          onClose={() => setShowSuccessMessage(false)}
        />
      )}
    </div>
  );
};

export default CreatePostModal;
